import { useEffect, useState } from "react";

export type ArticleCitationStyle =
  | "oscola"
  | "bluebook"
  | "harvard"
  | "apa-7"
  | "sa-citation";

/**
 * OSCOLA is the ONLY citation style permitted across the platform's legal
 * writing and research tools. The union type retains legacy slugs so stored
 * preferences still parse, but no other style is selectable.
 */
export const ARTICLE_CITATION_OPTIONS: {
  slug: ArticleCitationStyle;
  label: string;
  short: string;
  note: string;
}[] = [
  {
    slug: "oscola",
    label: "OSCOLA",
    short: "OSCOLA",
    note: "Oxford Standard for the Citation of Legal Authorities — the required style for all submissions.",
  },
];

const STORAGE_KEY = "ndlcs.article.citationStyle";

export function useCitationStyle(defaultStyle: ArticleCitationStyle = "oscola") {
  const [style, setStyle] = useState<ArticleCitationStyle>(defaultStyle);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (
        saved &&
        ARTICLE_CITATION_OPTIONS.some((o) => o.slug === saved)
      ) {
        setStyle(saved as ArticleCitationStyle);
      }
    } catch {
      /* ignore */
    }
    setHydrated(true);
  }, []);

  const update = (next: ArticleCitationStyle) => {
    setStyle(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  return { style, setStyle: update, hydrated };
}

export function citationRuleFor(style: ArticleCitationStyle): {
  placement: string;
  bibliographyLabel: string;
  keyRule: string;
} {
  switch (style) {
    case "oscola":
      return {
        placement: "Every authority in a numbered footnote (never in-text).",
        bibliographyLabel:
          "Bibliography — Primary sources (cases, legislation) then Secondary (books, journals).",
        keyRule:
          "Italicise case names, not statutes. Pinpoint paragraphs in [square brackets].",
      };
    case "bluebook":
      return {
        placement:
          "Footnotes for scholarly writing; in-text 'string cites' allowed only where the journal permits.",
        bibliographyLabel:
          "No bibliography required — all authorities appear in footnotes; Table of Authorities for briefs.",
        keyRule:
          "Follow Bluebook Rule 10 (cases) and Rule 12 (statutes). Signals (See, See also, Cf.) matter.",
      };
    case "sa-citation":
      return {
        placement: "Footnotes for authorities; short-form after first citation.",
        bibliographyLabel:
          "List of authorities: Cases, Legislation, Books, Journal articles, Internet sources.",
        keyRule:
          "Italicise party names, not 'v'. Section as 's 23'. Paragraph pinpoints for post-2000 judgments.",
      };
    case "harvard":
      return {
        placement:
          "In-text (Author Year) or (Author Year, p. X) for direct quotes — no footnotes for authorities.",
        bibliographyLabel:
          "Reference list at end, alphabetical by surname, hanging indent.",
        keyRule:
          "Use 'and' between two authors, 'et al.' for three or more. Cases still cited in SA form.",
      };
    case "apa-7":
      return {
        placement: "(Surname, Year) in text; page numbers only for quotes.",
        bibliographyLabel:
          "References list at end, alphabetical, hanging indent. Include DOIs.",
        keyRule:
          "'&' inside parentheses, 'and' in narrative. Only first word of titles capitalised.",
      };
  }
}
